import React, { Fragment } from 'react';
import { Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ComposedChart } from 'recharts';

// Simple hash -> deterministic color
const palette = [
  '#1b6dba',
  '#E53E3E',
  '#805AD5',
  '#DD6B20',
  '#38A169',
  '#D69E2E',
  '#319795',
  '#d53f8c',
  '#3eade5',
  '#4B5563',
];
const hashString = (str) => {
  if (str == null) return 0;
  // Coerce to string (handles numbers, etc.)
  const s = String(str);
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return hash;
};
const colorFor = (k) => palette[hashString(k) % palette.length];

// Unified data structure: one array of issue objects with wave values
// For wording changes we keep separate keys (e.g. 'Cost of housing' vs 'Cost of housing & rent').
// If an issue was not asked in a wave leave the value undefined (will become null in chart rows).
const ISSUES = [
  { key: 'Homelessness', 'Sep 2023': 94, 'Jul 2025': 92 },
  { key: 'Open air drug use', 'Sep 2023': 84, 'Jul 2025': 81 },
  { key: 'Fentanyl dealing', 'Sep 2023': 81, 'Jul 2025': 81 },
  { key: 'Crime and lack of safety', 'Sep 2023': 79 },
  { key: 'Cost of housing & rent', 'Sep 2023': 79, 'Jul 2025': 80 },
  { key: 'Cost of living', 'Jul 2025': 83 },
  { key: 'Untreated mental health issues', 'Jul 2025': 82 },
  { key: 'Drug and substance abuse', 'Jul 2025': 75 },
  { key: 'Homeless encampments', 'Jul 2025': 70 },
  { key: 'Ineffectiveness in San Francisco City government', 'Sep 2023': 72 },
  { key: 'Corruption in San Francisco City government', 'Sep 2023': 50, 'Jul 2025': 50 },
  { key: 'The downtown San Francisco economy', 'Sep 2023': 68, 'Jul 2025': 63 },
  {
    key: 'The difficulty of opening or running a small business in San Francisco',
    'Sep 2023': 63,
    'Jul 2025': 62,
  },
  { key: 'The difficulty of building or renovating homes in San Francisco', 'Jul 2025': 60 },
  { key: 'Declining quality of life for residents', 'Jul 2025': 56 },
  { key: 'Property crime like burglaries and car break-ins', 'Jul 2025': 55 },
  { key: 'The quality of public education', 'Sep 2023': 57, 'Jul 2025': 53 },
  { key: 'Declining tax revenues', 'Jul 2025': 51 },
  { key: 'A shortage of police officers', 'Jul 2025': 49 },
  { key: 'Too little growth and development', 'Jul 2025': 49 },
  { key: 'Jobs & Unemployment', 'Sep 2023': 39, 'Jul 2025': 48 },
  { key: 'The condition of streets and roads', 'Sep 2023': 46 },
  { key: 'The availability of parking', 'Sep 2023': 36 },
  { key: 'Traffic congestion', 'Sep 2023': 27, 'Jul 2025': 26 },
  { key: 'A lack of access to public transportation', 'Sep 2023': 26, 'Jul 2025': 30 },
  { key: 'Too much growth & development', 'Sep 2023': 17, 'Jul 2025': 13 },
  { key: 'Violent crime', 'Sep 2023': 60, 'Jul 2025': 39 },
  { key: 'Influence of tech on City government', 'Jul 2025': 35 },
  { key: 'Safety on public transportation', 'Jul 2025': 46 },
];

const WAVES = ['Sep 2023', 'Jul 2025'];

const buildAllRows = () => {
  const rows = WAVES.map((wave) => ({ name: wave }));
  ISSUES.forEach((issue) => {
    WAVES.forEach((wave, idx) => {
      rows[idx][issue.key] = issue[wave] ?? null;
    });
  });
  return rows;
};

const ALL_ROWS = buildAllRows();
const ALL_ISSUE_KEYS = ISSUES.map((i) => i.key);

const RankedLinesChart = ({ title, data, issues }) => {
  const lastIndex = data.length - 1;
  // Identify issues whose value is unchanged across waves
  const constantIssues = issues.filter((issue) => {
    const first = data[0][issue];
    const last = data[lastIndex][issue];
    return first != null && last != null && first === last;
  });

  // Dynamic Y domain (trim empty headroom). Add small padding then round to nearest 5.
  const allValues = [];
  issues.forEach((issue) => {
    data.forEach((row) => {
      const v = row[issue];
      if (typeof v === 'number') allValues.push(v);
    });
  });
  let yMin = 0;
  let yMax = 100;
  if (allValues.length) {
    const rawMin = Math.min(...allValues);
    const rawMax = Math.max(...allValues);
    const pad = 3; // percentage padding
    yMin = Math.max(0, rawMin - pad);
    yMax = Math.min(100, rawMax + pad);
    // Round to nearest 5 for cleaner axis
    yMin = Math.floor(yMin / 5) * 5;
    yMax = Math.ceil(yMax / 5) * 5;
    if (yMin === yMax) {
      yMin = Math.max(0, yMin - 5);
      yMax = Math.min(100, yMax + 5);
    }
  }
  const range = yMax - yMin;
  const step = range <= 40 ? 5 : 10;
  const yTicks = [];
  for (let t = yMin; t <= yMax; t += step) yTicks.push(t);

  // Precompute the last (right-most) non-null index for each issue; if that's the left wave, we'll label there.
  const terminalIndexByIssue = {};
  issues.forEach((issue) => {
    for (let i = lastIndex; i >= 0; i--) {
      if (data[i][issue] != null) {
        terminalIndexByIssue[issue] = i;
        break;
      }
    }
  });

  const CustomTerminalLabel = (props) => {
    const { x, y, value, index, issue } = props;
    const terminalIndex = terminalIndexByIssue[issue];
    if (index !== terminalIndex || value == null) return null;
    const color = colorFor(issue);
    const isRight = terminalIndex === lastIndex;
    if (isRight) {
      // Right-side (latest wave) label: percentage left of dot, issue right of dot
      return (
        <g>
          <text
            x={x - 8}
            y={y + 4}
            textAnchor="end"
            fontSize={12}
            fontWeight={600}
            fill={color}
            stroke="#fff"
            strokeWidth={3}
            paintOrder="stroke"
          >
            {value}%
          </text>
          <text
            x={x + 8}
            y={y + 4}
            textAnchor="start"
            fontSize={12}
            fill={color}
            stroke="#fff"
            strokeWidth={3}
            paintOrder="stroke"
          >
            {issue}
          </text>
        </g>
      );
    }
    // Left-only (no value in latest wave) label: put both percent and issue to the right of dot.
    return (
      <text
        x={x + 8}
        y={y + 4}
        textAnchor="start"
        fontSize={12}
        fill={color}
        stroke="#fff"
        strokeWidth={3}
        paintOrder="stroke"
      >
        {value}% {issue}
      </text>
    );
  };

  return (
    <Fragment>
      <p className="text-left text-gray-700 text-sm font-semibold uppercase mb-2">{title}</p>
      <ResponsiveContainer width="100%" height={800}>
        <ComposedChart data={data} margin={{ top: 20, right: 220, left: 10, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" axisLine={false} tickLine={false} />
          <YAxis
            type="number"
            domain={[yMin, yMax]}
            ticks={yTicks}
            allowDecimals={false}
            axisLine={false}
            tickLine={false}
            label={{
              value: '% Extremely/Very Serious',
              angle: -90,
              position: 'insideLeft',
              dy: 50,
            }}
          />
          {issues.map((issue) => (
            <Line
              key={issue}
              type="monotone"
              dataKey={issue}
              stroke={colorFor(issue)}
              strokeWidth={2}
              strokeDasharray={constantIssues.includes(issue) ? '4 4' : undefined}
              dot={{ r: 4, strokeWidth: 2, fill: colorFor(issue), stroke: '#fff' }}
              connectNulls={false}
              isAnimationActive={false}
              label={(props) => <CustomTerminalLabel {...props} issue={issue} />}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
      <p className="mt-2 text-xs text-gray-500 leading-snug">
        Percentages represent respondents rating the issue as an <em>Extremely</em> or <em>Very</em>{' '}
        serious problem in each wave. Labels are placed at the Jul 2025 values; lines break where an
        issue was not in that wave's list. Wording changes appear as separate series.
      </p>
    </Fragment>
  );
};

export const TopBottomIssuesBump = () => (
  <div className="flex flex-col gap-6">
    <RankedLinesChart
      title="All Tracked Issues: % Saying Issue is Extremely / Very Serious"
      data={ALL_ROWS}
      issues={ALL_ISSUE_KEYS}
    />
  </div>
);
export default TopBottomIssuesBump;
